/**
 * Single source of truth for a blot case's completion status.
 *
 * This lived duplicated in BlotRecentCases (correct, per-blot) and in
 * InProgressCases (broken: it keyed off `totalLanes`, which the blot pipeline
 * never populates — it writes `totalBlots` — so `expectedValidations` was always
 * 0 and no case could EVER reach 'validated'; every case stayed forever in the
 * home page's "Tâches à traiter"). Keep both views importing this function so
 * they cannot drift apart again.
 *
 *   - 'validated'   : every sample blot (non-control, non-hidden) has a manual verdict
 *   - 'in-progress' : control selected, calibration done, or some verdicts set
 *   - 'pending'     : nothing touched yet
 *
 * The per-blot rule applies in AI mode too: the "Valider" button exists in both
 * modes, so a control + calibration alone is only ever 'in-progress'.
 */
export type BlotCaseStatus = 'validated' | 'in-progress' | 'pending'

export interface BlotCaseStatusInput {
  blots?: any
  totalLanes?: number
  hiddenBlotIds?: number[] | null
  manualBlotResults?: Record<string, unknown> | null
  bandCalibration?: any
  selectedPositiveControlId?: number | null
  selectedPositiveControl2Id?: number | null
  selectedPositiveControl3Id?: number | null
}

export function computeBlotCaseStatus(a: BlotCaseStatusInput): BlotCaseStatus {
  const controlIds = new Set(
    [a.selectedPositiveControlId, a.selectedPositiveControl2Id, a.selectedPositiveControl3Id].filter(
      (id) => id != null
    )
  )
  const hiddenIds = new Set(a.hiddenBlotIds || [])
  const manual = a.manualBlotResults || {}
  const controlCount = controlIds.size
  const hasCalibration = !!a.bandCalibration && Object.keys(a.bandCalibration).length > 0

  // Sample blots = non-control, non-hidden. A case is fully "validated" only when
  // EVERY sample blot has been signed off by the biologist.
  const sampleBlots = (Array.isArray(a.blots) ? a.blots : []).filter(
    (b: any) => b && !controlIds.has(b.id) && !hiddenIds.has(b.id)
  )
  const validatedSamples = sampleBlots.filter((b: any) => manual[b.id] != null).length
  const manualCount = Object.keys(manual).length

  // Prefer the exact per-blot check; fall back to the lane count only when the
  // blot list isn't available (legacy rows).
  if (sampleBlots.length > 0) {
    if (validatedSamples === sampleBlots.length) return 'validated'
  } else {
    const expected = Math.max(0, (a.totalLanes || 0) - controlCount - hiddenIds.size)
    if (expected > 0 && manualCount >= expected) return 'validated'
  }

  if (validatedSamples > 0 || manualCount > 0 || controlCount > 0 || hasCalibration) return 'in-progress'
  return 'pending'
}
